import React, { useState, useEffect } from 'react';
import Card from '../Shared/Card';
import Button from '../Shared/Button';
import Toast from '../Shared/Toast';
import ActionMenu, { ActionMenuItem } from '../Shared/ActionMenu';
import ImageGenerationModal from '../Shared/ImageGenerationModal';
import { Copy, Download, RefreshCw, MessageSquare, Image as ImageIcon } from 'lucide-react';
import AIService from '../../services/aiService';
import { getStaticUrl } from '../../config';

const PostPreview = ({
  content,
  hashtags = [],
  wordCount = 0,
  onSave,
  onRegenerate,
  onFeedback
}) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'info' });
  const [imagePrompts, setImagePrompts] = useState([]);
  const [imagePromptsLoading, setImagePromptsLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [imageServiceAvailable, setImageServiceAvailable] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const getCharacterStatus = (count) => {
    if (count < 1300) return { color: 'text-yellow-600', message: 'قصير جداً' };
    if (count <= 2000) return { color: 'text-green-600', message: 'مثالي للينكد إن' };
    if (count <= 3000) return { color: 'text-yellow-600', message: 'طويل قليلاً' };
    return { color: 'text-red-600', message: 'طويل جداً - قد يتم اقتطاعه' };
  };

  const characterCount = content?.length || 0;
  const charStatus = getCharacterStatus(characterCount);

  const handleCopyToClipboard = () => {
    const fullText = `${content}\n\n${hashtags.join(' ')}`;
    navigator.clipboard.writeText(fullText).then(() => {
      setToast({ isVisible: true, message: 'تم نسخ المنشور!', type: 'success' });
    });
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        content,
        hashtags,
        wordCount,
        characterCount
      });
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) {
      setToast({ isVisible: true, message: 'الرجاء كتابة ملاحظاتك', type: 'warning' });
      return;
    }

    setFeedbackLoading(true);
    try {
      if (onFeedback) {
        await onFeedback(feedback);
        setFeedback('');
        setShowFeedback(false);
        setToast({ isVisible: true, message: 'تم تحسين المنشور بنجاح!', type: 'success' });
      }
    } catch (error) {
      setToast({ isVisible: true, message: 'فشل في تحسين المحتوى: ' + error.message, type: 'error' });
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleGenerateImagePrompts = async () => {
    if (!content) {
      setToast({ isVisible: true, message: 'لا يوجد محتوى لإنشاء صور له', type: 'warning' });
      return;
    }

    setImagePromptsLoading(true);
    try {
      const response = await AIService.generateImagePrompts({ content });
      if (response.success && response.data.prompts) {
        // Parse prompts from [PROMPT_1] tags
        const promptTexts = [];
        const matches = response.data.prompts.matchAll(/\[PROMPT_(\d+)\]\s*([\s\S]*?)\s*\[\/PROMPT_\1\]/g);
        for (const match of matches) {
          promptTexts.push(match[2].trim());
        }

        if (promptTexts.length > 0) {
          setImagePrompts(promptTexts);
          setToast({ isVisible: true, message: 'تم إنشاء وصف الصور!', type: 'success' });
        } else {
          setToast({ isVisible: true, message: 'لم يتم العثور على وصف للصور', type: 'warning' });
        }
      }
    } catch (error) {
      setToast({ isVisible: true, message: 'فشل في إنشاء وصف الصور: ' + error.message, type: 'error' });
    } finally {
      setImagePromptsLoading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!content) {
      setToast({ isVisible: true, message: 'لا يوجد محتوى لإنشاء صورة له', type: 'warning' });
      return;
    }

    if (!imageServiceAvailable) {
      setToast({ isVisible: true, message: 'خدمة إنشاء الصور غير مُهيأة (يتطلب HF_TOKEN)', type: 'warning' });
      return;
    }

    setImageGenerating(true);
    try {
      // Use a temporary ID if post not saved yet
      const tempPostId = Date.now();
      const response = await AIService.generateImage(tempPostId, content);

      if (response.success && response.data.imagePath) {
        setGeneratedImage(response.data.imagePath);
        setToast({ isVisible: true, message: 'تم إنشاء الصورة بنجاح!', type: 'success' });
      }
    } catch (error) {
      setToast({ isVisible: true, message: 'فشل في إنشاء الصورة: ' + error.message, type: 'error' });
    } finally {
      setImageGenerating(false);
    }
  };

  const handleDownloadImage = () => {
    if (!generatedImage) return;

    const link = document.createElement('a');
    link.href = getStaticUrl(generatedImage);
    link.download = generatedImage.split('/').pop();
    link.click();
    setToast({ isVisible: true, message: 'تم تحميل الصورة!', type: 'success' });
  };

  // Check if image generation service is available
  useEffect(() => {
    const checkService = async () => {
      try {
        const response = await AIService.checkImageServiceStatus();
        setImageServiceAvailable(response.data.available);
      } catch (error) {
        console.error('Failed to check image service:', error);
        setImageServiceAvailable(false);
      }
    };

    checkService();
  }, []);

  if (!content) {
    return (
      <Card>
        <div className="text-center py-12 sm:py-16 text-gray-500">
          <div className="inline-block p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl mb-4">
            <MessageSquare size={40} className="text-blue-400 sm:w-14 sm:h-14" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2">لا توجد معاينة بعد</h3>
          <p className="text-sm sm:text-base text-gray-500 px-4">قم بملء النموذج وتوليد المنشور لرؤية المعاينة هنا</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Toast
        {...toast}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />

      <div className="space-y-4">
        {/* Preview Card */}
        <Card>
          <div className="space-y-4 sm:space-y-5">
            {/* LinkedIn-style preview */}
            <div className="border-2 border-blue-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 bg-gradient-to-br from-white to-blue-50/30 shadow-inner">
              <div className="flex items-center mb-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full blur opacity-30"></div>
                  <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg text-sm sm:text-base">
                    U
                  </div>
                </div>
                <div className="me-3 sm:me-4">
                  <div className="font-bold text-gray-900 text-sm sm:text-base">اسمك</div>
                  <div className="text-xs sm:text-sm text-gray-600">صانع محتوى</div>
                </div>
              </div>

              <div className="whitespace-pre-wrap text-gray-900 leading-relaxed text-sm sm:text-[15px]">
                {content}
              </div>

              {hashtags.length > 0 && (
                <div className="mt-4 pt-4 border-t border-blue-100">
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {hashtags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-1 sm:px-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs sm:text-sm font-medium rounded-full shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-r from-blue-50/50 to-cyan-50/50 rounded-xl">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-xs sm:text-sm text-gray-700">
                  <span className="font-semibold text-blue-600">{wordCount}</span> كلمة
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                <span className={`text-xs sm:text-sm ${charStatus.color} font-medium`}>
                  <span className="font-semibold">{characterCount}</span> حرف - {charStatus.message}
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Action Buttons - Reorganized Hierarchy */}
        <Card>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Primary Action - Most Prominent */}
            <Button
              onClick={handleCopyToClipboard}
              variant="primary"
              className="flex-1 sm:flex-none min-w-[140px]"
            >
              <Copy size={16} className="me-2" />
              نسخ المنشور
            </Button>

            {/* Secondary Actions */}
            <Button
              onClick={handleSave}
              variant="secondary"
              className="flex-1 sm:flex-none"
            >
              <Download size={16} className="me-2" />
              حفظ
            </Button>

            {/* Tertiary Actions - in Dropdown for cleaner UI */}
            <ActionMenu align="right">
              <ActionMenuItem
                icon={MessageSquare}
                onClick={() => setShowFeedback(!showFeedback)}
              >
                تحسين المحتوى
              </ActionMenuItem>
              <ActionMenuItem
                icon={ImageIcon}
                onClick={() => setShowImageModal(true)}
              >
                إضافة صورة
              </ActionMenuItem>
              {onRegenerate && (
                <ActionMenuItem
                  icon={RefreshCw}
                  onClick={onRegenerate}
                >
                  إعادة التوليد
                </ActionMenuItem>
              )}
            </ActionMenu>
          </div>
        </Card>

        {/* Feedback Section */}
        {showFeedback && (
          <Card>
            <div className="space-y-4">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">
                كيف تريد تحسين المنشور؟
              </h3>

              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="مثال: اجعله أكثر حماساً، أضف أمثلة عملية، اجعله أقصر..."
                className="textarea-field h-24 text-sm sm:text-base"
                maxLength={500}
              />

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <span className="text-xs sm:text-sm text-gray-500">
                  {feedback.length} / 500 حرف
                </span>

                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    onClick={() => setShowFeedback(false)}
                    variant="outline"
                    size="small"
                    className="flex-1 sm:flex-none"
                  >
                    إلغاء
                  </Button>
                  <Button
                    onClick={handleFeedbackSubmit}
                    loading={feedbackLoading}
                    variant="primary"
                    size="small"
                    className="flex-1 sm:flex-none"
                  >
                    تحسين المنشور
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Image Generation Modal */}
      <ImageGenerationModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        content={content}
        imageServiceAvailable={imageServiceAvailable}
        onGeneratePrompts={handleGenerateImagePrompts}
        onGenerateImage={handleGenerateImage}
        promptsLoading={imagePromptsLoading}
        imageGenerating={imageGenerating}
        prompts={imagePrompts}
        generatedImage={generatedImage}
        onDownloadImage={handleDownloadImage}
        toast={setToast}
      />
    </>
  );
};

export default PostPreview;
